import React from 'react';
import { motion } from 'framer-motion';
import { Star, Eye, Zap, ShieldCheck, CheckCircle2, Flame } from 'lucide-react';
export const Hero = () => {
  return <section className="relative pt-20 pb-16 md:pt-28 md:pb-24 overflow-hidden" data-id="element-101">
      {/* Background glow */}
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-full max-w-4xl h-full bg-gold-500/5 blur-[120px] rounded-full pointer-events-none" data-id="element-102" />

      <div className="container mx-auto px-4 relative z-10" data-id="element-103">
        <div className="flex flex-col items-center text-center max-w-4xl mx-auto" data-id="element-104">
          {/* Title */}
          <motion.h1 initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.6
        }} className="text-2xl md:text-5xl lg:text-6xl font-serif font-bold mb-4 text-white" data-id="element-105">
            ⚖️ Dejá de empezar cada reclamo desde cero
          </motion.h1>
          <motion.h2 initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.6,
          delay: 0.1
        }} className="text-xl md:text-3xl font-serif font-bold mb-6 text-gold-500" data-id="element-106">
            Tratado Práctico de Defensa del Consumidor 2026
          </motion.h2>

          {/* Mockup Image */}
          <motion.div initial={{
          opacity: 0,
          y: 24,
          scale: 0.96
        }} animate={{
          opacity: 1,
          y: 0,
          scale: 1
        }} transition={{
          duration: 0.7,
          delay: 0.15
        }} className="w-full max-w-2xl my-6 md:my-8" data-id="element-107">
            <img src="https://cdn.magicpatterns.com/uploads/sHJiyUGY1ZVsYvAeS1gk8W/c7929282-51b5-4e43-8684-f530f006bc0d.png" alt="Tratado Práctico de Defensa del Consumidor 2026: tomo principal rodeado de modelos editables, demandas, reclamos administrativos, medidas cautelares y bonos profesionales" className="w-full h-auto drop-shadow-2xl" data-id="element-108" />
          </motion.div>

          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.6,
          delay: 0.3
        }} className="flex flex-col items-center w-full" data-id="element-109">
            <p className="md:text-xl text-slate-300 font-medium mb-8 text-[14px] font-[Poppins,_sans-serif]" data-id="element-110">
              Todo lo que necesitás para resolver reclamos de consumo con mayor
              rapidez, seguridad y respaldo jurídico.
            </p>

            {/* Pills */}
            <div className="flex flex-wrap justify-center items-center gap-2 sm:gap-3 mb-6 max-w-full" data-id="element-111">
              {['Modelos editables', 'Demandas', 'Reclamos administrativos', 'Medidas cautelares', 'Jurisprudencia actualizada', 'Estrategias procesales', 'Material listo para aplicar'].map((pill, idx) => <div key={idx} className="flex items-center gap-1.5 sm:gap-2 bg-legal-800 border border-legal-700 rounded-full px-3 sm:px-4 py-2 text-xs sm:text-sm text-slate-300 whitespace-nowrap shrink-0" data-id="element-112">
                  <CheckCircle2 className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-gold-500 shrink-0" data-id="element-113" />
                  {pill}
                </div>)}
            </div>

            {/* Rating */}
            <div className="flex items-center gap-2 mb-8" data-id="element-114">
              <div className="flex gap-0.5" data-id="element-115">
                {[...Array(5)].map((_, i) => <Star key={i} className="w-5 h-5 fill-gold-500 text-gold-500" data-id="element-116" />)}
              </div>
              <span className="text-slate-300 font-semibold text-sm" data-id="element-117">
                4.9 (500+ ventas)
              </span>
            </div>

            {/* Offer / price anchor card */}
            <div className="w-full max-w-md bg-legal-800 border-2 border-gold-500 rounded-2xl p-6 md:p-8 mb-8 shadow-[0_0_40px_rgba(201,162,74,0.15)]" data-id="element-118">
              <p className="text-sm text-gold-500 font-semibold uppercase tracking-wider mb-3" data-id="element-119">
                Hoy con descuento exclusivo por tiempo limitado
              </p>
              <div className="flex items-end justify-center gap-3 mb-4" data-id="element-120">
                <span className="text-slate-500 line-through decoration-2 text-xl" data-id="element-121">
                  AR$ 184.950
                </span>
                <span className="font-serif text-4xl md:text-5xl font-bold text-white leading-none" data-id="element-122">
                  AR$ 24.990
                </span>
              </div>

              <ul className="space-y-2 text-left mb-4" data-id="element-123">
                {['🎁 Bono #1: Biblioteca de 100 Modelos Editables — GRATIS', '🎁 Bono #2: Sistema Express de Evaluación de Casos — GRATIS', '🎁 Bono #3: Kit Profesional de Cartas Documento — GRATIS'].map((b, i) => <li key={i} className="text-sm text-slate-300" data-id="element-124">
                    {b}
                  </li>)}
              </ul>

              <div className="bg-gold-500/10 border border-gold-500/30 rounded-lg py-2 px-3 mb-4" data-id="element-125">
                <p className="text-gold-500 font-bold text-sm" data-id="element-126">
                  Ahorrás AR$ 196.440 (89% OFF)
                </p>
              </div>

              <p className="flex items-center justify-center gap-2 text-red-400 font-semibold text-sm" data-id="element-127">
                <Flame className="w-4 h-4" data-id="element-128" />
                Quedan 7 cupos a este precio
              </p>
            </div>

            {/* CTA */}
            <motion.a href="#oferta" whileHover={{
            scale: 1.02
          }} whileTap={{
            scale: 0.98
          }} className="inline-flex items-center justify-center w-full sm:w-auto px-8 py-4 bg-gold-500 hover:bg-gold-400 text-legal-900 font-bold rounded-lg text-lg transition-colors shadow-[0_0_20px_rgba(201,162,74,0.3)]" data-id="element-129">
              QUIERO MI TRATADO AHORA
            </motion.a>

            {/* Trust Badges */}
            <div className="mt-8 flex flex-wrap justify-center items-center gap-4 sm:gap-6 text-sm text-slate-400" data-id="element-130">
              <div className="flex items-center gap-1.5" data-id="element-131">
                <Zap className="w-4 h-4 text-gold-500" data-id="element-132" />
                Entrega inmediata
              </div>
              <div className="flex items-center gap-1.5" data-id="element-133">
                <ShieldCheck className="w-4 h-4 text-gold-500" data-id="element-134" />
                Pago seguro
              </div>
              <div className="flex items-center gap-1.5" data-id="element-135">
                <CheckCircle2 className="w-4 h-4 text-gold-500" data-id="element-136" />
                Garantía de 7 días
              </div>
            </div>

            <p className="mt-6 flex items-center justify-center gap-2 text-sm text-slate-400" data-id="element-137">
              <Eye className="w-4 h-4 text-gold-500" data-id="element-138" />
              24 profesionales viendo este producto ahora
            </p>
          </motion.div>
        </div>
      </div>
    </section>;
};